import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, EvidenceBadge } from "@/components/shared";
import { SubmitStep } from "@/components/ApplyDialog";
import { api, errMsg, label, ago } from "@/lib/api";

function Timeline({ appId, onClose }) {
  const [app, setApp] = useState(null);
  useEffect(() => { if (appId) { api.get(`/applications/${appId}`).then((r) => setApp(r.data)); } }, [appId]);
  return (
    <Dialog open={!!appId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="application-timeline-dialog">
        <DialogHeader><DialogTitle>{app?.job_snapshot.title}</DialogTitle></DialogHeader>
        {app && (
          <div className="space-y-4">
            <EvidenceBadge value={app.evidence} />
            {app.evidence_details?.receipt_id && <p className="font-mono text-xs" data-testid="application-receipt-id">Receipt: {app.evidence_details.receipt_id}</p>}
            <ol className="space-y-3 border-l pl-4">{app.events.map((e, i) => (
              <li key={i} className="text-sm"><p className="font-medium">{label(e.type)} <span className="text-xs font-normal text-muted-foreground">· {e.actor} · {ago(e.at)}</span></p><p className="text-xs text-muted-foreground break-all">{e.detail}</p></li>
            ))}</ol>
            {app.status === "prepared" && <div className="border-t pt-4"><SubmitStep app={app} onDone={onClose} /></div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Applications() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.get("/applications").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.post(`/applications/${id}/status`, { status }); toast.success("Status updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const counts = ["confirmed", "candidate_reported", "unconfirmed"].map((k) => [k, rows.filter((r) => r.evidence === k).length]);
  return (
    <div>
      <PageHeader eyebrow="Evidence ledger" title="Applications" />
      <div className="mb-6 flex flex-wrap gap-3">{counts.map(([k, n]) => <div key={k} className="flex items-center gap-2"><EvidenceBadge value={k} testid={`evidence-count-${k}`} /><span className="font-mono text-sm">{n}</span></div>)}</div>
      <Panel className="overflow-x-auto p-0" testid="applications-table">
        <Table>
          <TableHeader><TableRow><TableHead>Role</TableHead><TableHead>Mode</TableHead><TableHead>Evidence</TableHead><TableHead>Status</TableHead><TableHead>Fit</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No applications yet.</TableCell></TableRow>}
            {rows.map((a) => (
              <TableRow key={a.application_id} data-testid={`application-row-${a.application_id}`}>
                <TableCell><p className="font-medium">{a.job_snapshot.title}</p><p className="text-xs text-muted-foreground">{a.job_snapshot.employer} · {ago(a.created_at)}{a.duplicate_acknowledged && " · duplicate acknowledged"}</p></TableCell>
                <TableCell className="capitalize">{label(a.mode)}</TableCell>
                <TableCell><EvidenceBadge value={a.evidence} /></TableCell>
                <TableCell>
                  {a.status === "prepared" ? <span className="text-sm text-muted-foreground">Prepared</span> : (
                    <Select value={a.status} onValueChange={(v) => setStatus(a.application_id, v)}>
                      <SelectTrigger className="h-8 w-36 bg-white" data-testid={`application-status-${a.application_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{["submitted", "interviewing", "offer", "rejected", "withdrawn"].map((s) => <SelectItem key={s} value={s} disabled={s === "submitted"}>{label(s)}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="font-mono">{a.match_score}</TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={() => setView(a.application_id)} data-testid={`application-view-${a.application_id}`}>{a.status === "prepared" ? "Submit" : "Timeline"}</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
      <p className="mt-4 text-xs text-muted-foreground">Interview, offer and rejection statuses are reported by you. We never infer or invent employer decisions.</p>
      <Timeline appId={view} onClose={() => { setView(null); load(); }} />
    </div>
  );
}
